import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import { DocumentTextIcon, SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';

export interface BrandingGuidelines {
  titleGeneration: string;
  descriptionGuidelines: string;
  styleAndTone: string;
  keywordStrategy: string;
  materialFocus: string;
  qualityEmphasis: string;
}

export const parseGuidelinesText = (text: string): BrandingGuidelines => {
  // Parse the backend guidelines text into structured format
  const lines = text.split('\n');
  let titleGeneration = '';
  let descriptionGuidelines = '';
  let styleAndTone = '';
  let keywordStrategy = '';
  let materialFocus = '';
  let qualityEmphasis = '';
  
  let currentSection = '';
  let currentContent = '';
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Check for section headers
    if (trimmedLine.includes('**Titel-Generierung**') || trimmedLine.includes('Titel-Generierung') || trimmedLine.includes('Töpferei-Stil und Branding-Richtlinien')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'titleGeneration';
      currentContent = '';
    } else if (trimmedLine.includes('**Beschreibung**') || trimmedLine.includes('Beschreibung') || trimmedLine.includes('Beschreibungen:')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'descriptionGuidelines';
      currentContent = '';
    } else if (trimmedLine.includes('**Stil und Tonalität**') || trimmedLine.includes('Stil und Tonalität') || trimmedLine.includes('Stil und Ton')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'styleAndTone';
      currentContent = '';
    } else if (trimmedLine.includes('**Keyword-Strategie**') || trimmedLine.includes('Keyword-Strategie')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'keywordStrategy';
      currentContent = '';
    } else if (trimmedLine.includes('**Material-Fokus**') || trimmedLine.includes('Material-Fokus')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'materialFocus';
      currentContent = '';
    } else if (trimmedLine.includes('**Qualitäts-Betonung**') || trimmedLine.includes('Qualitäts-Betonung')) {
      if (currentSection && currentContent) {
        assignToSection(currentSection, currentContent);
      }
      currentSection = 'qualityEmphasis';
      currentContent = '';
    } else if (trimmedLine && !trimmedLine.startsWith('###') && !trimmedLine.startsWith('Die Bilder zeigen')) {
      // Add content to current section
      if (currentContent) {
        currentContent += '\n' + trimmedLine;
      } else {
        currentContent = trimmedLine;
      }
    }
  }
  
  // Don't forget the last section
  if (currentSection && currentContent) {
    assignToSection(currentSection, currentContent);
  }
  
  function assignToSection(section: string, content: string) {
    const cleanContent = content.trim();
    switch (section) {
      case 'titleGeneration':
        titleGeneration = cleanContent;
        break;
      case 'descriptionGuidelines':
        descriptionGuidelines = cleanContent;
        break;
      case 'styleAndTone':
        styleAndTone = cleanContent;
        break;
      case 'keywordStrategy':
        keywordStrategy = cleanContent;
        break;
      case 'materialFocus':
        materialFocus = cleanContent;
        break;
      case 'qualityEmphasis':
        qualityEmphasis = cleanContent;
        break;
    }
  }
  
  return {
    titleGeneration: titleGeneration || 'Keine Richtlinien für Titel-Generierung gefunden',
    descriptionGuidelines: descriptionGuidelines || 'Keine Beschreibungs-Richtlinien gefunden',
    styleAndTone: styleAndTone || 'Keine Stil-Richtlinien gefunden',
    keywordStrategy: keywordStrategy || 'Keine Keyword-Strategie gefunden',
    materialFocus: materialFocus || 'Keine Material-Fokus Richtlinien gefunden',
    qualityEmphasis: qualityEmphasis || 'Keine Qualitäts-Betonung Richtlinien gefunden'
  };
};

export const convertGuidelinesToText = (guidelines: BrandingGuidelines): string => {
  return `
**Titel-Generierung**
${guidelines.titleGeneration}

**Beschreibung**
${guidelines.descriptionGuidelines}

**Stil und Tonalität**
${guidelines.styleAndTone}

**Keyword-Strategie**
${guidelines.keywordStrategy}

**Material-Fokus**
${guidelines.materialFocus}

**Qualitäts-Betonung**
${guidelines.qualityEmphasis}
  `.trim();
};

const BrandingSettings: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [guidelines, setGuidelines] = useState<BrandingGuidelines>({
    titleGeneration: '',
    descriptionGuidelines: '',
    styleAndTone: '',
    keywordStrategy: '',
    materialFocus: '',
    qualityEmphasis: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    loadGuidelines();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function for robust token acquisition - uses idToken for backend API
  const getIdToken = async () => {
    const tokenRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      account: accounts[0],
    };

    try {
      // Try silent token acquisition first
      const response = await instance.acquireTokenSilent(tokenRequest);
      // Use idToken for backend API calls - backend validates token audience as client_id
      return response.idToken || response.accessToken;
    } catch (error) {
      console.warn('Silent token acquisition failed, trying interactive:', error);
      
      try {
        // Fallback to interactive token acquisition
        const response = await instance.acquireTokenPopup(tokenRequest);
        return response.idToken || response.accessToken;
      } catch (interactiveError) {
        console.error('Interactive token acquisition failed:', interactiveError);
        throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.');
      }
    }
  };

  const loadGuidelines = async () => {
    setLoading(true);
    try {
      const idToken = await getIdToken();

      const guidelinesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/branding/guidelines`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (guidelinesResponse.ok) {
        const result = await guidelinesResponse.json();
        if (result.structured) {
          setGuidelines({
            titleGeneration: result.structured.titleGeneration ?? '',
            descriptionGuidelines: result.structured.descriptionGuidelines ?? '',
            styleAndTone: result.structured.styleAndTone ?? '',
            keywordStrategy: result.structured.keywordStrategy ?? '',
            materialFocus: result.structured.materialFocus ?? '',
            qualityEmphasis: result.structured.qualityEmphasis ?? ''
          });
        } else if (result.guidelines) {
          const parsedGuidelines = parseGuidelinesText(result.guidelines);
          setGuidelines(parsedGuidelines);
        }
      }
    } catch (error) {
      console.error('Failed to load guidelines:', error);
      toast.error('Fehler beim Laden der Richtlinien');
    } finally {
      setLoading(false);
    }
  };



  const handleGuidelineChange = (field: keyof BrandingGuidelines, value: string) => {
    setGuidelines(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const saveGuidelines = async () => {
    setSaving(true);
    try {
      const idToken = await getIdToken();

      const saveResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/branding/guidelines`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guidelines: {
            structured: guidelines,
            markdown: convertGuidelinesToText(guidelines)
          }
        }),
      });

      if (saveResponse.ok) {
        setHasChanges(false);
        setLastSaved(new Date());
        toast.success('Markenrichtlinien erfolgreich gespeichert!');
      } else {
        const error = await saveResponse.json();
        toast.error(`Fehler beim Speichern: ${error.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Failed to save guidelines:', error);
      toast.error('Fehler beim Speichern der Richtlinien');
    } finally {
      setSaving(false);
    }
  };



  const resetToDefaults = () => {
    setGuidelines({
      titleGeneration: 'Jedes Produkt erhält einen einzigartigen, prägnanten Titel. Format: "[Produkttyp] [Besonderheit]" z.B. "Handgedrehte Schale mit Verlaufsglasur"',
      descriptionGuidelines: 'Beschreibungen betonen die Einzigartigkeit und Handarbeit. Beginne mit einer sensorischen Beschreibung, erkläre die Technik, nenne praktische Details und Pflegehinweise.',
      styleAndTone: 'Warm und authentisch, professionell aber nahbar. Vermeide generische E-Commerce-Sprache. Verwende reichhaltige, beschreibende Begriffe für Materialien und Texturen.',
      keywordStrategy: 'handgefertigt, unikat, keramik, töpferware, handgemacht, kunsthandwerk, nachhaltig, einzigartig, künstlerisch, authentisch',
      materialFocus: 'Betone die Qualität der verwendeten Tone und Glasuren. Beschreibe Herkunft und Verarbeitung. Erwähne besondere Eigenschaften wie Lebensmittelechtheit oder Frostbeständigkeit.',
      qualityEmphasis: 'Jedes Stück ist ein Unikat mit bewussten "Imperfektionen" die den Charakter ausmachen. Betone die Langlebigkeit und zeitloses Design.'
    });
    setHasChanges(true);
    toast('Richtlinien auf Standardwerte zurückgesetzt', { icon: 'ℹ️' });
  };

  const previewGuidelines = () => {
    const text = convertGuidelinesToText(guidelines);
    console.log('Guidelines Preview:', text);
    toast.success('Vorschau in der Konsole angezeigt');
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-pottery-earth sm:text-3xl sm:truncate">
            Markenrichtlinien
          </h2>
          <p className="mt-1 text-sm text-clay-600">
            Konfigurieren Sie die KI-Prompts für die Generierung von Titeln, Beschreibungen und Keywords.
            Diese Richtlinien werden bei der Analyse von Produktbildern verwendet.
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={previewGuidelines}
            className="inline-flex items-center px-4 py-2 border border-clay-300 rounded-md shadow-sm text-sm font-medium text-clay-700 bg-pottery-cream hover:bg-clay-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            Vorschau
          </button>
          <button
            onClick={resetToDefaults}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500"
          >
            Standard
          </button>
        </div>
      </div>

      {/* Save Status */}
      {lastSaved && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex items-center">
            <CheckIcon className="h-5 w-5 text-green-400 mr-2" />
            <span className="text-sm text-green-800">
              Zuletzt gespeichert: {lastSaved.toLocaleString('de-DE')}
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-flex items-center">
            <SparklesIcon className="animate-spin h-5 w-5 text-clay-600 mr-2" />
            <span className="text-clay-600">Richtlinien werden geladen...</span>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Title Generation */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Titel-Generierung
              </h3>
              <textarea
                rows={6}
                value={guidelines.titleGeneration}
                onChange={(e) => handleGuidelineChange('titleGeneration', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Richtlinien für die Generierung von Produkttiteln..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Definieren Sie, wie Produkttitel generiert werden sollen. Geben Sie Beispiele und bevorzugte Begriffe an.
              </p>
            </div>
          </div>

          {/* Description Guidelines */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Beschreibungs-Richtlinien
              </h3>
              <textarea
                rows={6}
                value={guidelines.descriptionGuidelines}
                onChange={(e) => handleGuidelineChange('descriptionGuidelines', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Richtlinien für die Generierung von Produktbeschreibungen..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Legen Sie fest, wie Produktbeschreibungen strukturiert und formuliert werden sollen.
              </p>
            </div>
          </div>

          {/* Style and Tone */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Stil und Tonalität
              </h3>
              <textarea
                rows={5}
                value={guidelines.styleAndTone}
                onChange={(e) => handleGuidelineChange('styleAndTone', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Definieren Sie den gewünschten Stil und Ton der Texte..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Beschreiben Sie die gewünschte Tonalität und den Schreibstil für alle generierten Texte.
              </p>
            </div>
          </div>

          {/* Keyword Strategy */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Keyword-Strategie
              </h3>
              <textarea
                rows={5}
                value={guidelines.keywordStrategy}
                onChange={(e) => handleGuidelineChange('keywordStrategy', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Listen Sie wichtige Keywords und Begriffe auf..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Definieren Sie wichtige Keywords und Begriffe, die in Tags und Beschreibungen verwendet werden sollen.
              </p>
            </div>
          </div>

          {/* Material Focus */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Material-Fokus
              </h3>
              <textarea
                rows={5}
                value={guidelines.materialFocus}
                onChange={(e) => handleGuidelineChange('materialFocus', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Beschreiben Sie, wie Materialien präsentiert werden sollen..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Legen Sie fest, wie Materialien, Glasuren und Herstellungsverfahren beschrieben werden sollen.
              </p>
            </div>
          </div>

          {/* Quality Emphasis */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Qualitäts-Betonung
              </h3>
              <textarea
                rows={5}
                value={guidelines.qualityEmphasis}
                onChange={(e) => handleGuidelineChange('qualityEmphasis', e.target.value)}
                className="block w-full border-warmGray-300 rounded-md shadow-sm focus:ring-clay-600 focus:border-clay-600 sm:text-sm"
                placeholder="Definieren Sie, wie Qualität kommuniziert werden soll..."
              />
              <p className="mt-2 text-sm text-gray-500">
                Beschreiben Sie, wie die Qualität und Einzigartigkeit der handgefertigten Produkte betont werden soll.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveGuidelines}
              disabled={!hasChanges || saving}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clay-500 disabled:opacity-50 ${
                hasChanges 
                  ? 'bg-clay-600 hover:bg-clay-700' 
                  : 'bg-gray-400'
              }`}
            >
              {saving ? (
                <>
                  <SparklesIcon className="animate-spin h-5 w-5 mr-2" />
                  Speichern...
                </>
              ) : (
                <>
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {hasChanges ? 'Änderungen speichern' : 'Gespeichert'}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-12 bg-clay-50 rounded-lg p-6">
        <h4 className="text-sm font-medium text-clay-900 mb-3">Verwendung der Richtlinien</h4>
        <div className="text-sm text-clay-700 space-y-2">
          <p>• Diese Richtlinien werden als Prompt-Injektionen bei der KI-Analyse verwendet</p>
          <p>• Verwenden Sie konkrete Beispiele und klare Anweisungen</p>
          <p>• Die Richtlinien werden für alle Sprachen (Deutsch, Englisch, Französisch, Spanisch, Niederländisch) angepasst</p>
          <p>• Änderungen werden sofort bei der nächsten Analyse wirksam</p>
          <p>• Testen Sie verschiedene Formulierungen, um optimale Ergebnisse zu erzielen</p>
        </div>
      </div>
    </div>
  );
};

export default BrandingSettings; 